import React, { useContext, useState, useEffect } from "react";
import {
  Stack,
  Box,
  Grid2 as Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";

//Icons
import { BiMinusCircle } from "react-icons/bi";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { CREATE_CLASS, UPDATE_CLASS } from "../../Schema/Class";

import {
  SelectAcademicYear,
  SelectSchool,
  SelectGroup,
  SelectLevel,
  SelectTeacher,
} from "../../Function/DynamicSelect";

export const ClassForm = ({
  open,
  handleClose,
  setRefetch,
  dialogTitle,
  editData,
}) => {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  const [academicYearVal, setAcademicYearVal] = useState({ id: "", title: "" });
  const [groupVal, setGroupVal] = useState({ id: "", title: "" });
  const [levelVal, setLevelVal] = useState({ id: "", title: "" });
  const [schoolVal, setSchoolVal] = useState({ id: "", title: "" });
  const [teacherVal, setTeacherVal] = useState({ id: "", title: "" });

  //======================== Create User =========================
  const [createClass] = useMutation(CREATE_CLASS, {
    onCompleted: ({ createClass }) => {
      // console.log("createClass", createClass);
      setLoading(false);
      if (createClass?.is_success === true) {
        setAlert(true, "success", createClass?.message);
        handleClose();
        resetForm();
        setRefetch();
        setAcademicYearVal({ id: "", title: "" });
        setTeacherVal({ id: "", title: "" });
        setSchoolVal({ id: "", title: "" });
        setLevelVal({ id: "", title: "" });
        setGroupVal({ id: "", title: "" });
      } else {
        setAlert(true, "error", createClass?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });
  const [updateClass] = useMutation(UPDATE_CLASS, {
    onCompleted: ({ updateClass }) => {
      // console.log("updateClass", updateClass);
      setLoading(false);
      if (updateClass?.is_success === true) {
        setAlert(true, "success", updateClass?.message);
        handleClose();
        setRefetch();
      } else {
        setAlert(true, "error", updateClass?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    academic_year_id: Yup.string().required(t("require")),
    school_id: Yup.string().required(t("require")),
    group_id: Yup.string().required(t("require")),
    level_id: Yup.string().required(t("require")),
    teacher_id: Yup.string().required(t("require")),
    remark: null,
  });

  const formik = useFormik({
    initialValues: {
      academic_year_id: "",
      school_id: "",
      group_id: "",
      level_id: "",
      teacher_id: "",
      remark: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      // console.log("values:::", values);
      setLoading(true);
      if (dialogTitle === "Create") {
        createClass({
          variables: {
            input: {
              academic_year_id: values?.academic_year_id,
              school_id: values?.school_id,
              group_id: values?.group_id,
              level_id: values?.level_id,
              teacher_id: values?.teacher_id,
              remark: values?.remark,
            },
          },
        });
      } else {
        updateClass({
          variables: {
            id: editData?._id,
            input: {
              academic_year_id: values?.academic_year_id,
              school_id: values?.school_id,
              group_id: values?.group_id,
              level_id: values?.level_id,
              teacher_id: values?.teacher_id,
              remark: values?.remark,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  // console.log("errors", errors);

  useEffect(() => {
    setFieldValue("academic_year_id", academicYearVal?.id ?? "");
  }, [academicYearVal?.id]);
  useEffect(() => {
    setFieldValue("school_id", schoolVal?.id ?? "");
  }, [schoolVal?.id]);
  useEffect(() => {
    setFieldValue("group_id", groupVal?.id ?? "");
  }, [groupVal?.id]);
  useEffect(() => {
    setFieldValue("level_id", levelVal?.id ?? "");
  }, [levelVal?.id]);
  useEffect(() => {
    setFieldValue("teacher_id", teacherVal?.id ?? "");
  }, [teacherVal?.id]);

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
      setAcademicYearVal({ id: "", title: "" });
      setTeacherVal({ id: "", title: "" });
      setSchoolVal({ id: "", title: "" });
      setLevelVal({ id: "", title: "" });
      setGroupVal({ id: "", title: "" });
    }
  }, [open]);

  useEffect(() => {
    if (editData && dialogTitle === "Update") {
      setFieldValue("remark", editData?.remark);
      setAcademicYearVal({
        id: editData?.academic_year_id?._id,
        title: editData?.academic_year_id?.academic_year,
      });
      setTeacherVal({
        id: editData?.teacher_id?._id,
        title:
          language === "en"
            ? editData?.teacher_id?.latin_name
            : editData?.teacher_id?.last_name +
              " " +
              editData?.teacher_id?.first_name,
      });
      setSchoolVal({
        id: editData?.school_id?._id,
        title:
          language === "en"
            ? editData?.school_id?.school_name_en
            : editData?.school_id?.school_name,
      });
      setLevelVal({
        id: editData?.level_id?._id,
        title: editData?.level_id?.level_name,
      });
      setGroupVal({
        id: editData?.group_id?._id,
        title: editData?.group_id?.group_name,
      });
    }
  }, [editData]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {dialogTitle === "Create"
                ? t("dialog_title_create_class")
                : t("dialog_title_update_class")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("academic_year")}
                </Typography>
                <SelectAcademicYear
                  selectedVal={academicYearVal}
                  setSelectedVal={setAcademicYearVal}
                />
                {!!errors.academic_year_id && touched.academic_year_id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.academic_year_id}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("school")}
                </Typography>
                <SelectSchool
                  selectedVal={schoolVal}
                  setSelectedVal={setSchoolVal}
                />
                {!!errors.school_id && touched.school_id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.school_id}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("grade_level")}
                </Typography>
                <SelectLevel
                  selectedVal={levelVal}
                  setSelectedVal={setLevelVal}
                />
                {!!errors.level_id && touched.level_id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.level_id}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("group")}
                </Typography>
                <SelectGroup
                  selectedVal={groupVal}
                  setSelectedVal={setGroupVal}
                />
                {!!errors.group_id && touched.group_id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.group_id}
                  </FormHelperText>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("teacher")}
                </Typography>
                <SelectTeacher
                  selectedVal={teacherVal}
                  setSelectedVal={setTeacherVal}
                />
                {!!errors.teacher_id && touched.teacher_id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.teacher_id}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("remark")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  rows={2}
                  placeholder={t("remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions sx={{ border: "none" }}>
        <Box className="create-container">
          <Grid container>
            <Grid size={{ xs: 12 }}>
              {loading ? (
                <Button
                  className={language === "kh" ? "btn-create-kh" : "btn-create"}
                  fullWidth
                >
                  {t("loading")}
                </Button>
              ) : (
                <Button
                  className={language === "kh" ? "btn-create-kh" : "btn-create"}
                  fullWidth
                  onClick={handleSubmit}
                >
                  {dialogTitle === "Create" ? t("btn-create") : t("btn-update")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
